import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useContactModal } from '@/contexts/ContactModalProvider'
import { useUser } from '@clerk/clerk-react'
import { useAuth } from '@/contexts/AuthProvider'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xeeovkeg'

export function ContactModal() {
  const { isOpen, closeContact } = useContactModal()
  const { user: clerkUser } = useUser()
  const { currentOrg, currentUser } = useAuth()

  const defaultEmail = useMemo(() => {
    // Clerk type differs depending on config; keep it defensive.
    // @ts-ignore
    return (
      clerkUser?.primaryEmailAddress?.emailAddress ||
      // @ts-ignore
      clerkUser?.emailAddresses?.[0]?.emailAddress ||
      ''
    )
  }, [clerkUser])

  const [kind, setKind] = useState<'support' | 'feedback' | ''>('')
  const [email, setEmail] = useState('')
  const [details, setDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    // No default: user must explicitly choose Support vs Feedback each time.
    setKind('')
    setDetails('')
    setSubmitting(false)
    setSent(false)
    setError(null)
  }, [isOpen, defaultEmail])

  // Auto-fill signed-in user's email (but never overwrite if the user already typed something).
  useEffect(() => {
    if (!isOpen) return
    if (!defaultEmail) return
    setEmail((prev) => (prev ? prev : defaultEmail))
  }, [isOpen, defaultEmail])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return

    setSubmitting(true)
    setError(null)

    try {
      const fd = new FormData()
      fd.append('type', kind)
      fd.append('email', email)
      fd.append('details', details)

      // Helpful context
      if (currentUser?.id) fd.append('appUserId', String(currentUser.id))
      if (currentOrg?.id) fd.append('orgId', String(currentOrg.id))
      if (currentOrg?.name) fd.append('orgName', String(currentOrg.name))
      if (clerkUser?.id) fd.append('clerkUserId', String(clerkUser.id))
      fd.append('_subject', `[DB8AI.com] ${kind === 'support' ? 'Support' : 'Feedback'} request`)

      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      })

      if (!res.ok) {
        const txt = await res.text().catch(() => '')
        throw new Error(txt || `Form submission failed (${res.status})`)
      }

      setSent(true)
    } catch (err: any) {
      setError(err?.message || 'Failed to send message.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeContact()}>
      <DialogContent
        size="full"
        className="w-[92vw] sm:!w-[960px] sm:!max-w-[960px] text-black dark:text-white"
      >
        <DialogTitle className="text-xl font-bold">Feedback / Support</DialogTitle>

        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="radio"
                  name="contactKind"
                  value="support"
                  checked={kind === 'support'}
                  onChange={() => setKind('support')}
                />
                Support
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="radio"
                  name="contactKind"
                  value="feedback"
                  checked={kind === 'feedback'}
                  onChange={() => setKind('feedback')}
                />
                Feedback
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactEmail">Your Email</Label>
            <Input
              id="contactEmail"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactDetails">Details</Label>
            <Textarea
              id="contactDetails"
              required
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={
                kind === 'support'
                  ? 'What happened? What did you expect? Any error message?'
                  : kind === 'feedback'
                    ? 'What feedback do you have? What should we change/add?'
                    : 'Choose Support or Feedback above, then write your message…'
              }
              className="min-h-[140px]"
            />
          </div>

          {error ? <div className="text-sm text-red-600 dark:text-red-400">{error}</div> : null}
          {sent ? (
            <div className="text-sm text-green-700 dark:text-green-400">
              Sent — thank you. We’ll get back to you.
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={closeContact} disabled={submitting}>
              Close
            </Button>
            <Button type="submit" disabled={submitting || sent || kind === ''}>
              {submitting ? 'Sending…' : sent ? 'Sent' : 'Send'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}


